/**
 * liveDevicesViewModel — « Choisis tes appareils » en pleine séance : la fenêtre que les chevrons du
 * micro et de la caméra ouvraient (`TrackToggleWithDeviceSelectM`, `MediaDeviceSelectM`,
 * `MediaDeviceSelectModal`).
 *
 * Pur. Deux choses :
 *  - **la fenêtre** (`liveDevicesReducer`) : le chevron du micro l'ouvre sur le micro, celui de la
 *    caméra sur la caméra ; un onglet se touche ; un appareil choisi du genre qui l'a ouverte la
 *    referme (l'ancien la fermait quand l'appareil actif de ce genre changeait), un autre genre la
 *    laisse ouverte ;
 *  - **les listes** (`liveDeviceItems`) : les noms anglais du navigateur traduits comme l'ancien
 *    (`translateDeviceLabel`), « Par défaut du système » sous le premier, ou sous celui dont
 *    l'identifiant le dit ; un appareil sans nom (permission pas encore donnée) a un numéro.
 */
import { type RoomDevice } from "../../../../../engine/live/room/roomDevices"
import { type UILiveDevice, type UILiveDeviceTab } from "../../../../../design-system/templates/LiveDevicesSheet"

export type LiveDevicesState = { open: boolean; tab: UILiveDeviceTab; from: UILiveDeviceTab | null }

export type LiveDevicesEvent =
    | { type: "OPEN"; from: "microphone" | "camera" }
    | { type: "TAB"; tab: UILiveDeviceTab }
    | { type: "SELECTED"; tab: UILiveDeviceTab }
    | { type: "CLOSE" }

export const initialLiveDevicesState: LiveDevicesState = { open: false, tab: "microphone", from: null }

export function liveDevicesReducer(state: LiveDevicesState, event: LiveDevicesEvent): LiveDevicesState {
    switch (event.type) {
        case "OPEN":
            return { open: true, tab: event.from, from: event.from }
        case "TAB":
            return state.open ? { ...state, tab: event.tab } : state
        case "SELECTED":
            return state.open && event.tab === state.from ? initialLiveDevicesState : state
        case "CLOSE":
            return state.open ? initialLiveDevicesState : state
        default:
            return state
    }
}

const SYSTEM_DEFAULT = "Par défaut du système"

/** `MediaDeviceSelectModal.translateDeviceLabel`, mot pour mot. */
export function translateDeviceLabel(label: string): string {
    return label
        .replace(/Built-in/gi, "Intégré")
        .replace(/Virtual/gi, "Virtuel")
        .replace(/Default/gi, "Par défaut")
        .replace(/Camera/gi, "Caméra")
        .replace(/Speaker/gi, "Haut-parleur")
        .replace(/Headphones/gi, "Écouteurs")
        .replace(/Headset/gi, "Casque")
}

export function liveDeviceItems(devices: RoomDevice[], noun: string): UILiveDevice[] {
    return devices.map((device, index) => {
        const isDefault = index === 0 || device.id.toLowerCase().includes("default")
        const label = device.label.trim() !== "" ? translateDeviceLabel(device.label) : `${noun} ${index + 1}`
        return { id: device.id, label, ...(isDefault ? { description: SYSTEM_DEFAULT } : {}) }
    })
}
